// Textos em pt-BR de status/motivo/evento da campanha de recuperação.
// Contrato: docs/superpowers/plans/2026-09-29-recuperacao-clientes-campanha.md (seção "Contratos da API").
// __t() por chamada (nunca em constante de módulo — idioma trocaria sem reload, ver
// invariant_admin_i18n_texto_pt_e_chave): as funções de label calculam o texto a cada render.
import { __t } from '../../../i18n/t';

export type CampaignStatus = 'running' | 'finished' | 'cancelled';
export type FinishReason = 'completed' | 'budget_reached' | 'cancelled';
export type EnrollmentStatus = 'active' | 'recovered' | 'returned' | 'expired' | 'limit_reached';
export type EventType =
  | 'created'
  | 'level_started'
  | 'level_advanced'
  | 'notification_scheduled'
  | 'budget_reached'
  | 'finished'
  | 'cancelled';

export type ChipColor = 'default' | 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info';

// Status da notificação (push/sms) de um nível — contrato: docs/CUSTOMER_RECOVERY_CAMPAIGN.md
// (seção "Status da notificação (push/sms) por nível") do pontuax-backend.
export type NotificationStatus =
  | 'not_configured'
  | 'no_active_recipients'
  | 'scheduling_failed'
  | 'not_started'
  | 'scheduled'
  | 'pending_send'
  | 'sent'
  | 'sent_with_errors'
  | 'failed'
  | 'canceled';

export type NotificationChannelType = 'push-notification' | 'sms';

export const NOTIFICATION_STATUS_COLOR: Record<NotificationStatus, ChipColor> = {
  not_configured: 'default',
  no_active_recipients: 'default',
  scheduling_failed: 'error',
  not_started: 'default',
  scheduled: 'info',
  pending_send: 'warning',
  sent: 'success',
  // Erro aqui é token de push vencido (app desinstalado ou notificação bloqueada) —
  // acontece em todo disparo grande; o envio saiu. Os não entregues aparecem nos números.
  sent_with_errors: 'success',
  failed: 'error',
  canceled: 'default',
};

export const notificationChannelLabel = (type: NotificationChannelType): string => {
  switch (type) {
    case 'push-notification':
      return __t('Notificação push');
    case 'sms':
      return __t('SMS');
    default:
      return type;
  }
};

/** `DD/MM às HH:mm`, no fuso do navegador (o mesmo padrão do resto da tela — admin roda em BRT). */
const formatShortDateTime = (iso?: string | null): string | null => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  const dd = String(d.getDate()).padStart(2, '0');
  const MM = String(d.getMonth() + 1).padStart(2, '0');
  const HH = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  return `${dd}/${MM} às ${HH}:${mm}`;
};

/** Frase do status da notificação de um nível (spec da tela — CLAUDE.md, pedido do Emílio em 30/09). */
export const notificationStatusLabel = (status: NotificationStatus, sendAt?: string | null): string => {
  const when = formatShortDateTime(sendAt);
  switch (status) {
    case 'not_configured':
      return __t('Sem notificação configurada');
    case 'no_active_recipients':
      return __t('Não enviada: nenhum cliente ativo');
    case 'scheduling_failed':
      return __t('Falha ao agendar');
    case 'not_started':
      return __t('Sai quando o nível começar');
    case 'scheduled':
      return when ? __t('Agendada para %{when}', { when }) : __t('Agendada');
    case 'pending_send':
      return __t('Aguardando envio');
    case 'sent':
      return when ? __t('Enviada em %{when}', { when }) : __t('Enviada');
    case 'sent_with_errors':
      return when ? __t('Enviada em %{when}', { when }) : __t('Enviada');
    case 'failed':
      return __t('Falhou');
    case 'canceled':
      return __t('Cancelada');
    default:
      return status;
  }
};

export const CAMPAIGN_STATUS_CHOICES: Array<{ id: CampaignStatus; name: string }> = [
  { id: 'running', name: 'Em andamento' },
  { id: 'finished', name: 'Finalizada' },
  { id: 'cancelled', name: 'Encerrada' },
];

export const ENROLLMENT_STATUS_LABELS: Record<EnrollmentStatus, string> = {
  active: 'Na campanha',
  recovered: 'Voltou com cupom',
  returned: 'Voltou sem cupom',
  expired: 'Não voltou',
  limit_reached: '—',
};

export const ENROLLMENT_STATUS_COLORS: Record<EnrollmentStatus, ChipColor> = {
  active: 'info',
  recovered: 'success',
  returned: 'warning',
  expired: 'default',
  limit_reached: 'default',
};

// Em que pé está o brinde do nível para aquele cliente — contrato: `rewardStatus` de
// GET /customer-recovery-rules/{id}/enrollments (CustomerRecoveryRewardStatus no backend).
// É informação SEPARADA de `status`, que só fala da volta: dá para ter conquistado o
// brinde e ainda estar "Na campanha", e dá para ter voltado sem nunca conquistar.
export type RewardStatus = 'pending' | 'earned' | 'redeemed' | 'missed';

export const REWARD_STATUS_LABELS: Record<RewardStatus, string> = {
  pending: 'Ainda não',
  earned: 'Conquistou',
  redeemed: 'Trocou o cupom',
  missed: 'Não conquistou',
};

export const REWARD_STATUS_COLORS: Record<RewardStatus, ChipColor> = {
  pending: 'default',
  earned: 'success',
  redeemed: 'success',
  missed: 'warning',
};

export const ENROLLMENT_STATUS_CHOICES: Array<{ id: EnrollmentStatus; name: string }> = [
  { id: 'active', name: ENROLLMENT_STATUS_LABELS.active },
  { id: 'recovered', name: ENROLLMENT_STATUS_LABELS.recovered },
  { id: 'returned', name: ENROLLMENT_STATUS_LABELS.returned },
  { id: 'expired', name: ENROLLMENT_STATUS_LABELS.expired },
];

interface CampaignSituation {
  status: CampaignStatus;
  finishedReason?: FinishReason | null;
  legacy?: boolean;
}

/** Texto da situação exibido em lista, cabeçalho e relatório (spec §8.3: "Em andamento / Finalizada / Encerrada / Orçamento atingido"). */
export const campaignSituationLabel = (campaign: CampaignSituation): string => {
  if (campaign.legacy) return 'Modelo antigo';
  if (campaign.status === 'running') return 'Em andamento';
  if (campaign.status === 'cancelled') return 'Encerrada';
  if (campaign.status === 'finished' && campaign.finishedReason === 'budget_reached') return 'Orçamento atingido';
  return 'Finalizada';
};

export const campaignSituationColor = (campaign: CampaignSituation): ChipColor => {
  if (campaign.legacy) return 'default';
  if (campaign.status === 'running') return 'info';
  if (campaign.status === 'cancelled') return 'error';
  if (campaign.status === 'finished' && campaign.finishedReason === 'budget_reached') return 'warning';
  return 'success';
};

interface RecoveryEvent {
  type: EventType;
  levelName?: string | null;
  employee?: { id: string; name: string } | null;
  payload?: Record<string, any> | null;
}

/** Frase pronta pra aba Auditoria (spec §8.3: "Fulano criou", "Nível 2 começou (automático)", …). */
export const eventSentence = (event: RecoveryEvent): string => {
  const who = event.employee?.name;
  const level = event.levelName;
  const payload = event.payload || {};

  switch (event.type) {
    case 'created':
      return who ? `${who} criou a campanha` : 'Campanha criada';
    case 'level_started':
      return level
        ? `${level} começou${who ? ` (iniciado por ${who})` : ' (automático)'}`
        : 'Nível iniciado';
    case 'level_advanced': {
      const affected = typeof payload.affected === 'number' ? ` — ${payload.affected} cliente(s) afetado(s)` : '';
      return payload.manual && who
        ? `${who} avançou para o nível ${payload.toLevelOrder ?? ''}${affected}`
        : `Avançou automaticamente para o nível ${payload.toLevelOrder ?? ''}${affected}`;
    }
    case 'notification_scheduled': {
      const recipients = typeof payload.recipients === 'number' ? payload.recipients : '—';
      return `Mensagem agendada para ${recipients} cliente(s)`;
    }
    case 'budget_reached':
      return 'Orçamento atingido — campanha encerrada';
    case 'finished':
      return 'Campanha finalizada';
    case 'cancelled': {
      const reason = payload.reason;
      return who
        ? `Encerrada por ${who}${reason ? `: ${reason}` : ''}`
        : `Encerrada${reason ? `: ${reason}` : ''}`;
    }
    default:
      return event.type;
  }
};

export type AudienceSegment = 'at_risk' | 'inactive' | 'custom';

export const segmentLabel = (segment?: AudienceSegment | null): string => {
  switch (segment) {
    case 'at_risk':
      return __t('Em risco');
    case 'inactive':
      return __t('Inativos');
    default:
      return __t('Personalizado');
  }
};

/** Faixa de dias sem abastecer do público — `maxDays` é exclusivo (60 a 90 = 60 a 89 dias). */
export const daysRangeLabel = (minDays: number, maxDays: number | null): string =>
  maxDays == null
    ? __t('%{min} dias ou mais', { min: minDays })
    : __t('%{min} a %{max} dias', { min: minDays, max: maxDays - 1 });

// Faixa da tabela "Volta por tempo sem abastecer" — contrato: `daysBands` de
// GET /customer-recovery-rules/{id} (daysBandMetrics em metrics.ts do pontuax-backend).
export type DaysBandKind = 'below_min' | 'range' | 'never_fueled';

export interface DaysBand {
  kind: DaysBandKind;
  fromDays: number | null;
  toDays: number | null;
  audience: number;
  returned: number;
  brought: number;
  returnRate: number;
}

export const daysBandLabel = (band: DaysBand): string => {
  if (band.kind === 'never_fueled') return __t('Nunca abasteceram');
  if (band.kind === 'below_min') return __t('Menos de %{n} dias', { n: band.toDays });
  return daysRangeLabel(band.fromDays ?? 0, band.toDays);
};

// Requisito do brinde de um nível — contrato: `levels[].reward.requirement` de
// GET /customer-recovery-rules/{id} (loadLevelRewards em levelGift.ts do pontuax-backend).
export interface LevelRequirement {
  type: string;
  value: number | null;
  valueType: string;
}

/** "Abastecer 20 litros", "Acumular R$ 100", "Qualquer abastecimento". */
export const requirementLabel = (requirement: LevelRequirement | null): string => {
  if (!requirement || requirement.value == null) return __t('Qualquer abastecimento');
  const amount = requirement.valueType === 'volume'
    ? __t('%{n} litros', { n: requirement.value })
    : `R$ ${Number(requirement.value).toFixed(2).replace('.', ',')}`;
  return requirement.type === 'accumulated_fillin'
    ? __t('Acumular %{amount}', { amount })
    : __t('Abastecer %{amount}', { amount });
};
